import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Plus,
  Megaphone,
  Mail,
  Globe,
  Instagram,
  Linkedin,
  Calendar,
  FileText,
  X,
  Loader2
} from 'lucide-react';
import api, { Campaign, Client } from '../services/api';

const OutreachPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    campaign_type: 'email' | 'social_twitter' | 'social_linkedin' | 'social_instagram' | 'content' | 'pr';
    status: 'draft' | 'scheduled' | 'active' | 'paused' | 'completed';
    description: string;
    client: string;
    start_date: string;
    end_date: string;
    budget: string;
    target_reach: number;
    tags: string[];
  }>({
    name: '',
    campaign_type: 'email',
    status: 'draft',
    description: '',
    client: '',
    start_date: '',
    end_date: '',
    budget: '',
    target_reach: 0,
    tags: []
  });
  const [tagInput, setTagInput] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [campaignsRes, clientsRes] = await Promise.all([
        api.getCampaigns({
          status: selectedStatus !== 'all' ? selectedStatus : undefined,
          campaign_type: selectedType !== 'all' ? selectedType : undefined,
          search: searchQuery || undefined
        }),
        api.getClients()
      ]);
      setCampaigns(campaignsRes.results);
      setClients(clientsRes.results);
      setError(null);
    } catch (err) {
      setError('Failed to load campaigns');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedStatus, selectedType, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        client: formData.client || null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        budget: formData.budget || null
      };

      if (editingCampaign) {
        await api.updateCampaign(editingCampaign.id, data);
      } else {
        await api.createCampaign(data);
      }
      setShowModal(false);
      setEditingCampaign(null);
      resetForm();
      fetchData();
    } catch (err) {
      console.error(err);
      alert('Failed to save campaign');
    }
  };

  const handleEdit = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      campaign_type: campaign.campaign_type as 'email' | 'social_twitter' | 'social_linkedin' | 'social_instagram' | 'content' | 'pr',
      status: campaign.status as 'draft' | 'scheduled' | 'active' | 'paused' | 'completed',
      description: campaign.description,
      client: campaign.client || '',
      start_date: campaign.start_date || '',
      end_date: campaign.end_date || '',
      budget: campaign.budget || '',
      target_reach: campaign.target_reach,
      tags: campaign.tags || []
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      try {
        await api.deleteCampaign(id);
        fetchData();
      } catch (err) {
        console.error(err);
        alert('Failed to delete campaign');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      campaign_type: 'email',
      status: 'draft',
      description: '',
      client: '',
      start_date: '',
      end_date: '',
      budget: '',
      target_reach: 0,
      tags: []
    });
    setTagInput('');
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'completed':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      default:
        return '';
    }
  };

  const getCampaignTypeDisplay = (type: string) => {
    const map: Record<string, string> = {
      'email': 'Email',
      'social_twitter': 'Twitter',
      'social_linkedin': 'LinkedIn',
      'social_instagram': 'Instagram',
      'content': 'Content',
      'pr': 'PR'
    };
    return map[type] || type;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <Mail className="w-5 h-5" />;
      case 'social_twitter':
        return <Globe className="w-5 h-5" />;
      case 'social_linkedin':
        return <Linkedin className="w-5 h-5" />;
      case 'social_instagram':
        return <Instagram className="w-5 h-5" />;
      case 'content':
        return <FileText className="w-5 h-5" />;
      case 'pr':
        return <Megaphone className="w-5 h-5" />;
      default:
        return <Globe className="w-5 h-5" />;
    }
  };

  if (loading && campaigns.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-black">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-base font-bold text-gray-900 dark:text-white">Outreach</h1>
              <p className="text-gray-600 dark:text-gray-300">Manage marketing and communication campaigns</p>
            </div>
            <button
              onClick={() => { resetForm(); setEditingCampaign(null); setShowModal(true); }}
              className="flex items-center gap-2 bg-primary-500 text-black px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
            >
              <Plus className="w-5 h-5" />
              New Campaign
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-3 py-3">
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-2">
            {error}
          </div>
        )}

        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-2">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search campaigns..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-4">
              <select
                className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="email">Email</option>
                <option value="social_twitter">Twitter</option>
                <option value="social_linkedin">LinkedIn</option>
                <option value="social_instagram">Instagram</option>
                <option value="content">Content</option>
                <option value="pr">PR</option>
              </select>
              <select
                className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Campaigns Grid */}
        {campaigns.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">No campaigns found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {campaigns.map((campaign) => (
              <div key={campaign.id} className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="p-3">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-primary-500">
                          {getTypeIcon(campaign.campaign_type)}
                        </span>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{campaign.name}</h3>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusClass(campaign.status)}`}>
                          {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                        </span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300">{campaign.description}</p>
                      {campaign.client_name && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Client: {campaign.client_name}</p>
                      )}
                    </div>
                  </div>

                  {/* Schedule & Info */}
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    {campaign.start_date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {new Date(campaign.start_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {getCampaignTypeDisplay(campaign.campaign_type)}
                      </span>
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mb-2">
                    <div className="grid grid-cols-4 gap-4">
                      <div className="text-center">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {campaign.actual_reach.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-300">Reach</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {campaign.engagement_rate}%
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-300">Engagement</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {campaign.conversions}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-300">Conversions</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                          {campaign.roi}%
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-300">ROI</p>
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  {campaign.tags && campaign.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {campaign.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-end gap-2 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <button
                      onClick={() => handleEdit(campaign)}
                      className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(campaign.id)}
                      className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg p-3 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                {editingCampaign ? 'Edit Campaign' : 'New Campaign'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                  <select
                    value={formData.campaign_type}
                    onChange={(e) => setFormData({ ...formData, campaign_type: e.target.value as 'email' | 'social_twitter' | 'social_linkedin' | 'social_instagram' | 'content' | 'pr' })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="email">Email Campaign</option>
                    <option value="social_twitter">Twitter Campaign</option>
                    <option value="social_linkedin">LinkedIn Campaign</option>
                    <option value="social_instagram">Instagram Campaign</option>
                    <option value="content">Content Campaign</option>
                    <option value="pr">PR Campaign</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'draft' | 'scheduled' | 'active' | 'paused' | 'completed' })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="draft">Draft</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Client (Optional)</label>
                <select
                  value={formData.client}
                  onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="">No client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Budget</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Reach</label>
                  <input
                    type="number"
                    value={formData.target_reach}
                    onChange={(e) => setFormData({ ...formData, target_reach: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    placeholder="Add a tag"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                    >
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-500 text-black rounded-lg hover:bg-primary-600"
                >
                  {editingCampaign ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutreachPage;
